<script lang="ts">
/** One tab in the bar. `key` is what comes back on `select`. */
export interface GaiaTabBarItem {
  key: string;
  label: string;
  /** Count after the label. `0` still renders — an empty tab should say so. */
  count?: number;
  /** Not reachable yet; renders dimmed and stays unclickable. */
  disabled?: boolean;
}

/**
 * Where the row sits, which is what its chrome follows:
 *
 * - `page` — under a page title (CM detail, Calendars). Body-sized labels, a
 *   hairline running the full width, counts as ink pills.
 * - `card` — the head of a card (File Detail's entities card). Tinted ground,
 *   taller rows, leading icons, scrolls sideways when the tabs outrun the card.
 * - `panel` — a side panel's head (Contract Detail's right rail). Denser, and
 *   the count is a mono figure rather than a pill, because a pill beside a
 *   12px label in a 336px rail reads as a button.
 */
export type GaiaTabBarVariant = 'page' | 'card' | 'panel';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaCountPill from '../base/GaiaCountPill.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFonts, gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  items: {
    type: Array as PropType<GaiaTabBarItem[]>,
    required: true,
  },
  /** Key of the active tab. */
  active: {
    type: String,
    required: true,
  },
  variant: {
    type: String as PropType<GaiaTabBarVariant>,
    default: 'page',
  },
});

const emit = defineEmits<{ select: [key: string] }>();

function pick(item: GaiaTabBarItem): void {
  if (!item.disabled) emit('select', item.key);
}

// n-flex writes gap, wrap and align inline, so all three have to be the
// per-variant value here rather than something CSS can override from below.
const gap = computed<number | [number, number]>(() => {
  if (props.variant === 'card') return 2;
  return props.variant === 'panel' ? 0 : [20, 6];
});

// Only the page row wraps: a toolbar beside page-level tabs drops to its own
// line on a narrow screen, where a card head scrolls instead and a side rail
// is never wide enough for the question to arise.
const wrapRow = computed(() => props.variant === 'page');

const alignRow = computed(() => {
  if (props.variant === 'card') return 'center';
  return props.variant === 'panel' ? 'stretch' : 'flex-end';
});

const idle = gaiaNeutral.textTertiary;
const ink = gaiaNeutral.ink;
const muted = gaiaNeutral.textMuted;
const rule = gaiaNeutral.borderDivider;
const cardGround = gaiaNeutral.surfaceSoft;
const pageSize = gaiaFontSize.body;
const panelSize = gaiaFontSize.control;
const countSize = gaiaFontSize.micro;
const monoFont = gaiaFonts.mono;
const medium = String(gaiaFontWeight.medium);
const semibold = String(gaiaFontWeight.semibold);
const bold = String(gaiaFontWeight.bold);
</script>

<template>
  <!-- The underline tab row the CM surfaces share, in its three placements.
       naive's n-tabs cannot express this weight: the hover colour, the 2px ink
       underline sitting on the row's own hairline, and a bold active label are
       none of them props — which is why this row was hand-rolled in three
       views before it became a component. -->
  <n-flex
    :align="alignRow"
    :size="gap"
    :wrap="wrapRow"
    role="tablist"
    class="gaia-tab-bar"
    :class="`gaia-tab-bar--${props.variant}`"
  >
    <button
      v-for="item in props.items"
      :key="item.key"
      type="button"
      role="tab"
      class="tab"
      :class="{ active: item.key === props.active, disabled: item.disabled }"
      :aria-selected="item.key === props.active"
      :disabled="item.disabled"
      @click="pick(item)"
    >
      <span v-if="$slots.icon" class="icon">
        <slot name="icon" :item="item" :active="item.key === props.active" />
      </span>
      {{ item.label }}
      <template v-if="item.count !== undefined">
        <!-- A pill would out-shout a 12px label in a side rail, so the panel
             variant states the number and stops. -->
        <gaia-text
          v-if="props.variant === 'panel'"
          class="count"
          size="micro"
          tone="muted"
          :style="{ fontFamily: monoFont }"
        >
          {{ item.count }}
        </gaia-text>
        <gaia-count-pill v-else :active="item.key === props.active">
          {{ item.count }}
        </gaia-count-pill>
      </template>
    </button>
    <n-flex
      v-if="$slots.actions"
      align="center"
      :size="8"
      :wrap="false"
      class="actions"
      :style="{ marginLeft: 'auto', flex: 'none' }"
    >
      <slot name="actions" />
    </n-flex>
  </n-flex>
</template>

<style scoped>
.gaia-tab-bar {
  /* The rule the active tab's underline sits on. The row draws it rather than
     the page, so a tab bar is always self-contained. */
  border-bottom: 1px solid v-bind(rule);
}

.tab {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: none;
  margin-bottom: -1px;
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  font-family: inherit;
  font-weight: v-bind(medium);
  color: v-bind(idle);
  cursor: pointer;
}

.tab:hover:not(.disabled) {
  color: v-bind(ink);
}

.tab.active {
  border-bottom-color: v-bind(ink);
  color: v-bind(ink);
}

.tab.disabled {
  color: v-bind(muted);
  cursor: default;
}

.icon {
  display: flex;
  flex: none;
}

/* page ------------------------------------------------------------------- */
.gaia-tab-bar--page .tab {
  padding: 0 2px 10px;
  font-size: v-bind(pageSize);
}

.gaia-tab-bar--page .tab.active {
  font-weight: v-bind(bold);
}

.gaia-tab-bar--page .actions {
  padding-bottom: 8px;
}

/* card ------------------------------------------------------------------- */
.gaia-tab-bar--card {
  padding: 0 12px;
  background: v-bind(cardGround);
  /* The tabs outrun a narrow card long before they should wrap onto a second
     row inside a card head, so the strip scrolls instead. */
  overflow-x: auto;
  scrollbar-width: none;
}

.gaia-tab-bar--card::-webkit-scrollbar {
  display: none;
}

.gaia-tab-bar--card .tab {
  gap: 8px;
  height: 42px;
  padding: 0 12px;
  font-size: inherit;
  white-space: nowrap;
}

.gaia-tab-bar--card .tab.active {
  font-weight: v-bind(semibold);
}

/* panel ------------------------------------------------------------------ */
.gaia-tab-bar--panel {
  padding: 0 14px;
}

.gaia-tab-bar--panel .tab {
  height: 44px;
  padding: 0 12px;
  font-size: v-bind(panelSize);
}

.gaia-tab-bar--panel .tab.active {
  font-weight: v-bind(bold);
}

/* The mono figure rides GaiaText's own inline colour channel, so only the
   metrics belong here. */
.gaia-tab-bar--panel .count {
  font-size: v-bind(countSize);
}

.gaia-tab-bar--panel .actions {
  align-self: center;
}
</style>
